"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Header = void 0;
const pixi_js_1 = require("pixi.js");
const Global_1 = require("../Global");
const home_svg_1 = __importDefault(require("../../assets/svg/home.svg"));
const arrow_left_svg_1 = __importDefault(require("../../assets/svg/arrow_left.svg"));
const Block_1 = require("./Block");
const SceneManager_1 = require("../managers/SceneManager");
const ui_1 = require("@pixi/ui");
const settings_1 = require("../config/settings");
const BrushScore_1 = require("./BrushScore");
const StreakScore_1 = require("./StreakScore");
const AssetManager_1 = __importDefault(require("../managers/AssetManager"));
class Header extends Block_1.Block {
    constructor(app, gameManager, sceneManager) {
        super(app);
        this.gameManager = gameManager;
        this.sceneManager = sceneManager;
        this.padding = {
            top: settings_1.Settings.titleSize / 3 + 2,
            right: 10,
            left: 34,
            bottom: 0
        };
        this.brushes = null;
        this.streak = null;
        this.name = 'header';
        this.zIndex = SceneManager_1.Layer.Header;
        this.x = 0;
        this.y = (0, Global_1.px)(this.padding.top);
        this.title = this.makeTitle();
        this.menu = this.makeMenu();
        this.brushScore = new BrushScore_1.BrushScore(this.app);
        this.brushButton = this.brushScore.asButton();
        this.sigs.push(this.brushButton.onPress.connect(() => __awaiter(this, void 0, void 0, function* () {
            if (this.sceneManager.currentScene.name !== SceneManager_1.Scenes.Atelier) {
                AssetManager_1.default.squishSound();
                yield this.sceneManager.routeTo(SceneManager_1.Scenes.Atelier);
            }
        })));
        this.streakScore = new StreakScore_1.StreakScore(this.app);
        this.streakButton = this.streakScore.asButton();
        this.sigs.push(this.streakButton.onPress.connect(() => __awaiter(this, void 0, void 0, function* () {
            // Just go nowhere for now...
        })));
        this.addChild(this.brushButton, this.streakButton);
        this.subs.add(gameManager.playerManager.playerData$.subscribe((pdata) => __awaiter(this, void 0, void 0, function* () {
            if (!pdata) {
                this.brushes = this.streak = null;
            }
            else {
                if (this.brushes !== null && this.streak !== null) {
                    yield (0, Global_1.sleep)(1.5);
                }
                if (this.brushes === null) {
                    this.brushes = pdata.brushes;
                    this.brushScore.set(this.brushes);
                }
                else if (pdata.brushes !== null && this.brushes !== pdata.brushes) {
                    const brushchange = pdata.brushes - this.brushes;
                    this.brushes = pdata.brushes;
                    yield this.brushScore.update(brushchange || 0);
                }
                if (this.streak === null) {
                    this.streak = pdata.streak;
                    this.streakScore.set(this.streak);
                }
                else if (pdata.streak !== null && this.streak !== pdata.streak) {
                    const streakchange = pdata.streak - this.streak;
                    this.streak = pdata.streak;
                    yield this.streakScore.update(streakchange || 0);
                }
            }
            this.layout();
        })));
        // TODO, just duplicate the header on the home screen for wide screens and hide it here
        this.subs.add(this.sceneManager.sceneWillChange$.subscribe((next) => __awaiter(this, void 0, void 0, function* () {
            if (this.isLandscapeBox) {
                if ((next === null || next === void 0 ? void 0 : next.name) !== SceneManager_1.Scenes.Home) {
                    yield this.series(() => (0, Global_1.fade)(this.title, 0));
                }
                else {
                    yield this.series(() => __awaiter(this, void 0, void 0, function* () {
                        yield (0, Global_1.fade)(this.title, 1, 0.4);
                    }));
                }
            }
        })));
    }
    makeTitle() {
        const text = new pixi_js_1.Text(settings_1.Settings.title, Block_1.Block.textStyle(settings_1.Settings.titleSize, false));
        return this.addChild(new ui_1.ButtonContainer(text));
    }
    makeMenu() {
        const btexture = pixi_js_1.Texture.from(arrow_left_svg_1.default);
        const bback = new pixi_js_1.Sprite(btexture);
        bback.anchor.set(0.5);
        const htexture = pixi_js_1.Texture.from(home_svg_1.default);
        const hbutton = new pixi_js_1.Sprite(htexture);
        hbutton.anchor.set(0.5);
        const button = new ui_1.ButtonContainer();
        button.addChild(hbutton, bback);
        button.name = 'homebutton';
        return this.addChild(button);
    }
    layout() {
        var _a, _b, _c;
        super.layout();
        const homevisible = ((_a = this.sceneManager.currentScene) === null || _a === void 0 ? void 0 : _a.backTo()) === SceneManager_1.Scenes.Home;
        (0, Global_1.fade)(this.menu.getChildAt(0), homevisible ? 1 : 0, 0.15).then();
        (0, Global_1.fade)(this.menu.getChildAt(1), homevisible ? 0 : 1, 0.15).then();
        if ((this.isLandscapeBox && ((_b = this.sceneManager.currentScene) === null || _b === void 0 ? void 0 : _b.name) !== SceneManager_1.Scenes.Home)
            || ((_c = this.sceneManager.currentScene) === null || _c === void 0 ? void 0 : _c.name) === SceneManager_1.Scenes.Tutorial) {
            this.title.alpha = 0;
            this.title.visible = false;
        }
        else {
            this.title.alpha = 1;
            this.title.visible = true;
        }
        const text = (this.title.getChildAt(0));
        if (text) {
            text.style.fontSize = (0, Global_1.px)(settings_1.Settings.titleSize * (this.overMediumSize ? 1.2 : 1));
            this.title.position.set((this.app.screen.width / 2) - (text.width / 2), 0);
        }
        this.menu.position.set((0, Global_1.left)(this.app.screen.width, this.padding.left, this.isLandscapeBox ? Infinity : settings_1.Settings.maxWidth), (0, Global_1.px)(this.padding.top));
        this.menu.scale.set((0, Global_1.px)(this.overSmallSize ? 0.25 : 0.21));
        this.streakButton.position.set((0, Global_1.right)(this.app.screen.width, this.streakButton.width / (0, Global_1.px)() + this.padding.right, this.isLandscapeBox ? Infinity : settings_1.Settings.maxWidth), (0, Global_1.px)(this.padding.top));
        this.brushButton.position.set(this.streakButton.x - this.brushButton.width, (0, Global_1.px)(this.padding.top));
        this.brushScore.layout();
        this.streakScore.layout();
    }
}
exports.Header = Header;
